/**
 * Dashboard Reducer
 * 
 * Manages UI state for the main dashboard.
 * Server state is handled by React Query.
 */

import type { DashboardState, DashboardAction } from "@/types/state";

/**
 * Initial dashboard UI state
 */
export const initialDashboardState: DashboardState = {
  // UI State
  searchQuery: "",
  emailVerified: true,
  createProjectDialogOpen: false,
  isProjectsOpen: (() => {
    const saved = localStorage.getItem("dashboard-projects-open");
    return saved !== null ? JSON.parse(saved) : true;
  })(),
  isBoardsOpen: (() => {
    const saved = localStorage.getItem("dashboard-boards-open");
    return saved !== null ? JSON.parse(saved) : true;
  })(),
  isActivityOpen: (() => {
    const saved = localStorage.getItem("dashboard-activity-open");
    return saved !== null ? JSON.parse(saved) : false;
  })(),
};

/**
 * Dashboard UI reducer
 */
export function dashboardReducer(
  state: DashboardState,
  action: DashboardAction
): DashboardState {
  switch (action.type) {
    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };

    case 'SET_EMAIL_VERIFIED':
      return { ...state, emailVerified: action.payload };

    case 'SET_CREATE_PROJECT_DIALOG_OPEN':
      return { ...state, createProjectDialogOpen: action.payload };

    case 'SET_PROJECTS_OPEN':
      localStorage.setItem("dashboard-projects-open", JSON.stringify(action.payload));
      return { ...state, isProjectsOpen: action.payload };

    case 'SET_BOARDS_OPEN':
      localStorage.setItem("dashboard-boards-open", JSON.stringify(action.payload));
      return { ...state, isBoardsOpen: action.payload };

    case 'SET_ACTIVITY_OPEN':
      localStorage.setItem("dashboard-activity-open", JSON.stringify(action.payload));
      return { ...state, isActivityOpen: action.payload };

    case 'RESET_DASHBOARD':
      return initialDashboardState;

    default:
      return state;
  }
}

/**
 * Action creators for cleaner dispatch calls
 */
export const dashboardActions = {
  setSearchQuery: (query: string): DashboardAction => ({
    type: 'SET_SEARCH_QUERY',
    payload: query,
  }),
  
  setEmailVerified: (verified: boolean): DashboardAction => ({
    type: 'SET_EMAIL_VERIFIED',
    payload: verified,
  }),
  
  setCreateProjectDialogOpen: (open: boolean): DashboardAction => ({
    type: 'SET_CREATE_PROJECT_DIALOG_OPEN',
    payload: open,
  }),
  
  setProjectsOpen: (open: boolean): DashboardAction => ({
    type: 'SET_PROJECTS_OPEN',
    payload: open,
  }),
  
  setBoardsOpen: (open: boolean): DashboardAction => ({
    type: 'SET_BOARDS_OPEN',
    payload: open,
  }),
  
  setActivityOpen: (open: boolean): DashboardAction => ({
    type: 'SET_ACTIVITY_OPEN',
    payload: open,
  }),
  
  resetDashboard: (): DashboardAction => ({
    type: 'RESET_DASHBOARD',
  }),
};
